import { Button, ErrorState } from '@insightai/ui'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router-dom'

/**
 * F0-62 — route-level error boundaries.
 *
 * Two mechanisms, because they catch different things:
 *   · `RouteErrorElement` handles loader/action errors and 404s raised by the
 *     router
 *   · `FeatureErrorBoundary` is a class boundary for render-time crashes inside
 *     a feature, so one broken module does not blank the whole console
 *
 * Scope matters: the boundary sits INSIDE the shell, so a crashed feature leaves
 * the sidebar usable and the user can navigate away instead of reloading.
 */
export function RouteErrorElement() {
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    const isForbidden = error.status === 403
    return (
      <div className="mx-auto flex max-w-lg flex-col gap-4 py-16">
        <ErrorState
          title={isForbidden ? "You don't have permission to view this" : "We couldn't find that page"}
          description={
            isForbidden
              ? 'Ask a workspace admin to change your role if you need access.'
              : 'The link may be out of date, or you may not have access to this workspace.'
          }
          detail={error.status === 404 ? undefined : String(error.status)}
        />
        <Button variant="secondary" asChild>
          <a href="/">Back to your workspace</a>
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-4 py-16">
      <ErrorState
        title="This screen stopped working"
        description="The error has been logged. Reloading usually fixes it."
        detail={error instanceof Error ? error.message : undefined}
        onRetry={() => window.location.reload()}
        retryLabel="Reload the page"
      />
    </div>
  )
}

interface State {
  error: Error | null
}

export class FeatureErrorBoundary extends Component<
  { children: ReactNode; feature: string },
  State
> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Replaced by the OpenTelemetry exporter when RUM lands (PE-10).
    console.error(`[${this.props.feature}] render error`, error, info.componentStack)
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="py-8">
          <ErrorState
            title={`The ${this.props.feature} view stopped working`}
            description="Other parts of the console are still available. The error has been logged."
            detail={this.state.error.message}
            onRetry={() => this.setState({ error: null })}
          />
        </div>
      )
    }
    return this.props.children
  }
}
